import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Award, CheckCircle2, XCircle, FileText } from "lucide-react";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import ConfirmationModal from "../../components/ConfirmationModal";
import { listReferralRewards, setReferralRewardCpPaid } from "../../services/referralAdmin";

const STATUS_STYLES = {
  PENDING_PAYMENT: "bg-amber-50 text-amber-700 border-amber-200",
  PENDING_VALIDATION: "bg-sky-50 text-sky-700 border-sky-200",
  REDEEMABLE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  REDEEMED: "bg-slate-100 text-slate-600 border-slate-200",
};

const STATUS_LABEL = {
  PENDING_PAYMENT: "Awaiting CP Payment",
  PENDING_VALIDATION: "Awaiting Validation",
  REDEEMABLE: "Redeemable",
  REDEEMED: "Redeemed",
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "PENDING_PAYMENT", label: "Awaiting CP Payment" },
  { key: "PENDING_VALIDATION", label: "To Validate" },
  { key: "REDEEMABLE", label: "CP Paid" },
];

const money = (v) => (v === null || v === undefined || v === "" ? "—" : v);

const ReferralRewards = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  // { reward, cpPaid } — pending validation/reject confirmation
  const [confirm, setConfirm] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "all") params.status = filter;
      const res = await listReferralRewards(params);
      setRows(Array.isArray(res) ? res : res?.items || []);
    } catch (e) {
      toast.error("Failed to load referral rewards");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const applyCpPaid = async () => {
    const action = confirm;
    setConfirm(null);
    if (!action) return;
    setSavingId(action.reward.id);
    try {
      await setReferralRewardCpPaid(action.reward.id, { cpPaid: action.cpPaid });
      toast.success(
        action.cpPaid ? "Validated — buyer can now redeem" : "Remittance rejected — returned to awaiting CP payment"
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update reward");
    } finally {
      setSavingId(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        key: "referrer",
        header: "Referrer → Referred",
        width: 200,
        render: (r) => (
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate">{r.referrerName || `User #${r.referrerUserId}`}</p>
            <p className="text-xs text-slate-500 truncate">Referred: {r.referredName || `User #${r.referredUserId}`}</p>
          </div>
        ),
      },
      { key: "project", header: "Project", width: 150, render: (r) => <span className="text-sm text-slate-700">{r.projectName || "—"}</span> },
      { key: "cp", header: "Channel Partner", width: 150, render: (r) => <span className="text-sm text-slate-700">{r.agentName || (r.agentId ? `CP #${r.agentId}` : "—")}</span> },
      {
        key: "commission",
        header: "Commission / Cut",
        width: 150,
        render: (r) => (
          <div className="min-w-0">
            <p className="text-sm text-slate-700">{money(r.commissionAmount)}</p>
            <p className="text-xs text-slate-500">{r.cutPercent != null ? `${r.cutPercent}% cut` : "—"}</p>
          </div>
        ),
      },
      { key: "amount", header: "Buyer Bonus", width: 110, render: (r) => <span className="text-sm font-semibold text-slate-900">{r.rewardAmount}</span> },
      {
        key: "remittance",
        header: "CP Remittance",
        width: 170,
        render: (r) =>
          r.cpRemittanceReference ? (
            <div className="min-w-0">
              <p className="text-sm text-slate-700 truncate">
                {money(r.cpRemittanceAmount)} · {r.cpRemittanceReference}
              </p>
              {r.cpRemittanceProofUrl ? (
                <a
                  href={r.cpRemittanceProofUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-flamingo-600 hover:underline"
                >
                  <FileText size={12} /> Proof
                </a>
              ) : null}
            </div>
          ) : (
            <span className="text-xs text-slate-400">Not submitted</span>
          ),
      },
      {
        key: "status",
        header: "Status",
        width: 150,
        render: (r) => (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold border ${STATUS_STYLES[r.status] || STATUS_STYLES.PENDING_PAYMENT}`}>
            {STATUS_LABEL[r.status] || r.status}
          </span>
        ),
      },
      {
        key: "action",
        header: "Validation",
        width: 190,
        align: "center",
        render: (r) => {
          if (r.status === "REDEEMED") return <span className="text-xs text-slate-400">Redeemed</span>;
          if (r.cpPaid) {
            return (
              <button
                disabled={savingId === r.id}
                onClick={() => setConfirm({ reward: r, cpPaid: false })}
                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold border bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 disabled:opacity-50"
                title="Revert CP-paid"
              >
                <CheckCircle2 size={14} /> CP Paid
              </button>
            );
          }
          return (
            <div className="flex items-center justify-center gap-1.5">
              <button
                disabled={savingId === r.id}
                onClick={() => setConfirm({ reward: r, cpPaid: true })}
                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold border bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50 disabled:opacity-50"
              >
                <CheckCircle2 size={14} /> Validate
              </button>
              {r.status === "PENDING_VALIDATION" ? (
                <button
                  disabled={savingId === r.id}
                  onClick={() => setConfirm({ reward: r, cpPaid: false })}
                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-semibold border bg-white text-rose-600 border-rose-200 hover:bg-rose-50 disabled:opacity-50"
                  title="Reject remittance"
                >
                  <XCircle size={14} />
                </button>
              ) : null}
            </div>
          );
        },
      },
    ],
    [savingId]
  );

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)]">
      <PageHeader
        icon={Award}
        title="Referral Rewards"
        subtitle="Buyer bonuses funded by the channel partner's cut. Validate a CP's remittance to unlock redemption for the buyer."
      />

      <div className="flex items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium border transition-colors ${
              filter === f.key
                ? "bg-flamingo-600 text-white border-flamingo-600"
                : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden p-5">
        <DataTable
          columns={columns}
          data={rows}
          loading={loading}
          minWidth={1180}
          rowKey={(r, i) => r?.id ?? i}
          emptyState={
            <div className="px-6 py-12 text-center">
              <Award size={28} className="mx-auto text-slate-300" />
              <p className="text-base font-semibold text-slate-900 mt-2">No referral rewards yet</p>
              <p className="text-sm text-slate-500 mt-1">Rewards appear here once a referred buyer completes a purchase.</p>
            </div>
          }
        />
      </div>

      <ConfirmationModal
        isOpen={!!confirm}
        onConfirm={applyCpPaid}
        onCancel={() => setConfirm(null)}
        message={
          confirm?.cpPaid
            ? `Confirm the channel partner's remittance is received? This unlocks redemption for the referring buyer.`
            : `Reject / revert this remittance? The reward returns to "Awaiting CP Payment".`
        }
      />
    </div>
  );
};

export default ReferralRewards;
